'use client';

import { useRouter } from '@/i18n/routing';
import { useAuth } from '@/lib/auth/use-auth';
import { useSearchParams } from 'next/navigation';
import { useState } from 'react';
import { checkUserExist, login, signup } from '../../actions/auth-action';
import { useHandleOtpState } from '../hooks/useHandleOtpState';
import Phone, { UserState } from './phone';
import VerifyOtp from './verify-otp';

type LoginWithOtpProps = {
  onStepChange?: (step: number) => void;
};

export default function LoginWithOtp({ onStepChange }: LoginWithOtpProps) {
  const [step, setStep] = useState(1);
  const [userData, setUserData] = useState<UserState>();
  const {
    otpError,
    handleOtpError,
    clearOtpError,
    disabledOtp,
    setDisabledOtp,
    isPending,
    setIsPending,
  } = useHandleOtpState();

  const { setAuth } = useAuth();
  const router = useRouter();
  const searchParams = useSearchParams();
  const redirectUrl = searchParams.get('redirect') ?? '/';
  const promo = searchParams.get('promo');

  const redirectPath = promo
    ? `${redirectUrl}?promo=${promo}`
    : `${redirectUrl}`;
  const newUserRedirectPath = promo
    ? `?redirect=${redirectUrl}&promo=${promo}`
    : `?redirect=${redirectUrl}`;

  const handleStepChange = (newStep: number) => {
    setStep(newStep);
    onStepChange?.(newStep);
  };

  const handleUserAuthentication = async (otp: string) => {
    if (!userData) {
      return;
    }
    let response;

    setIsPending(true);
    if (otpError) {
      clearOtpError();
    }

    if (userData.isNewUser) {
      response = await signup({
        code: otp,
        phone: userData.user.phone,
        dialCode: userData.user.dialCode,
        countryCode: userData.user.countryCode,
      });
    } else {
      response = await login({
        code: otp,
        phone: userData.user.phone,
        dialCode: userData.user.dialCode,
        countryCode: userData.user.countryCode,
      });
    }

    if (response.status === 'error') {
      setIsPending(false);

      handleOtpError(response.error.error);

      return;
    }

    if (response.status === 'success') {
      setAuth(
        {
          customerId: response.data.user.customerId,
          email: response.data.user.email ?? '',
          phone: response.data.user.phone ?? '',
          status: response.data.user.status ?? 'ACTIVE',
          picture: response.data.user.picture ?? '',
          id: response.data.user.id,
          name: response.data.user.name ?? '',
          gender: response.data.user.gender ?? '',
        },
        response.data.accessToken,
      );

      return userData.isNewUser
        ? router.push(`/complete-profile/${newUserRedirectPath}`)
        : router.push(redirectPath);
    }
  };

  const handleResendOtp = async () => {
    if (!userData) {
      return;
    }

    setIsPending(true);
    setDisabledOtp(false);
    clearOtpError();
    await checkUserExist(userData.user);
    setIsPending(false);
  };

  return (
    <div className="container grid items-center pt-10 justify-items-center">
      <div className="w-full">
        {step === 1 && (
          <Phone
            handleStep={() => handleStepChange(2)}
            handleUserState={data => setUserData(data)}
          />
        )}
        {step === 2 && userData && (
          <VerifyOtp
            handleSetStep={handleStepChange}
            phoneNumber={userData.user.phone}
            isPending={isPending}
            disabledOtp={disabledOtp}
            otpError={otpError}
            handleOtpComplete={handleUserAuthentication}
            handleResendOtp={handleResendOtp}
            // @ts-ignore
            otp={userData.otp}
          />
        )}
      </div>
    </div>
  );
}
